import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'react-toastify';
import {
  FaArrowLeft, FaArrowRight, FaCheck, FaSpinner, FaDownload,
  FaCheckCircle, FaExclamationTriangle, FaServer, FaNetworkWired,
  FaKey, FaClipboard
} from 'react-icons/fa';
import ProvisioningServiceSelector from '@/components/IT/ProvisioningServiceSelector';
import ProvisioningProgressDisplay from '@/components/IT/ProvisioningProgressDisplay';
import '@/styles/ITReportStyles.scss';
import './RouterSetupWizard.scss';

/**
 * Router Setup Wizard
 * Comprehensive multi-step wizard for registering, provisioning, and configuring routers
 * Step 1: Register Router - Basic router info and SSH credentials
 * Step 2: Take Control - SSH provisioning and service setup
 * Step 3: Configure VPN - WireGuard peer setup and management
 * Step 4: Verify & Complete - Final checks and summary
 */
const RouterSetupWizard = ({ onComplete, existingRouter = null }) => {
  const { apiFetch } = useAuth();

  // Wizard state
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [properties, setProperties] = useState([]);
  const [routerId, setRouterId] = useState(existingRouter?.id || null);
  const [isEditing] = useState(!!existingRouter);
  const [initialRouterData, setInitialRouterData] = useState(existingRouter || null);

  // Step 1: Register Router
  const [step1Data, setStep1Data] = useState({
    displayName: existingRouter?.displayName || '',
    ipAddress: existingRouter?.ipAddress || '',
    location: existingRouter?.location || '',
    propertyId: existingRouter?.propertyId || '',
    username: existingRouter?.username || 'admin',
    password: '', // Never pre-populate password from API (security)
    sshPort: existingRouter?.sshPort || 22,
    apiPort: existingRouter?.apiPort || 8728,
    useSsl: existingRouter?.useSsl || false
  });

  // Step 2: Select Services
  const [step2Data, setStep2Data] = useState({
    provisioningOptions: {
      enableWireGuard: existingRouter?.enableWireGuard ?? true,
      enableRadiusAuth: existingRouter?.enableRadiusAuth ?? false,
      enableSnmpMonitoring: existingRouter?.enableSnmpMonitoring ?? false,
      enableAddressListSync: existingRouter?.enableAddressListSync ?? false,
      enableFirewallTemplateSync: existingRouter?.enableFirewallTemplateSync ?? false
    }
  });

  // Step 3: Configure VPN
  const [step3Data, setStep3Data] = useState({
    wireguardConfig: null,
    wireguardLoading: false,
    newPeerPublicKey: '',
    newPeerEndpoint: '',
    newPeerKeepalive: '25',
    step3Status: 'idle', // idle, adding, success, failed
    step3Error: null,
    exportConfig: false
  });

  // Step 4: Verify & Provision
  const [step4Data, setStep4Data] = useState({
    isProvisioning: false,
    provisioningResult: null,
    verificationStatus: {},
    allChecksPass: false
  });

  // Load properties and full router details on mount
  useEffect(() => {
    loadProperties();
    if (isEditing && routerId) {
      loadRouterDetails();
    }
  }, []);

  const loadRouterDetails = async () => {
    setLoading(true);
    try {
      const response = await apiFetch(`/api/RouterConfig/${routerId}`);
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data) {
          const router = data.data;
          setInitialRouterData(router);
          // Update Step 2 provisioning options from full router data
          setStep2Data(prev => ({
            ...prev,
            provisioningOptions: {
              enableWireGuard: router.enableWireGuard ?? prev.provisioningOptions.enableWireGuard,
              enableRadiusAuth: router.enableRadiusAuth ?? prev.provisioningOptions.enableRadiusAuth,
              enableSnmpMonitoring: router.enableSnmpMonitoring ?? prev.provisioningOptions.enableSnmpMonitoring,
              enableAddressListSync: router.enableAddressListSync ?? prev.provisioningOptions.enableAddressListSync,
              enableFirewallTemplateSync: router.enableFirewallTemplateSync ?? prev.provisioningOptions.enableFirewallTemplateSync
            }
          }));
        }
      }
    } catch (err) {
      console.error('Error loading router details:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadProperties = async () => {
    try {
      const response = await apiFetch('/api/property/all');
      if (response.ok) {
        const data = await response.json();
        const allProperties = data.success && data.data ? data.data : [];
        // Filter to only active properties (using active or Active field)
        const activeProperties = allProperties.filter(prop => prop.active ?? prop.Active);
        setProperties(activeProperties);
      }
    } catch (err) {
      console.error('Error loading properties:', err);
    }
  };

  // ==================== STEP 1: Register Router ====================
  const handleStep1Change = (field, value) => {
    setStep1Data(prev => ({ ...prev, [field]: value }));
  };

  const validateStep1 = () => {
    const { displayName, ipAddress, username, password } = step1Data;
    if (!displayName || !ipAddress || !username) {
      toast.error('Please fill in all required fields');
      return false;
    }
    // When adding a new router, password is required
    // When editing, password is optional (blank = keep existing)
    if (!isEditing && !password) {
      toast.error('Please enter SSH password');
      return false;
    }
    return true;
  };

  const saveRouterConfig = async () => {
    if (!validateStep1()) return false;

    setSaving(true);
    try {
      const dto = {
        id: routerId || 0,
        displayName: step1Data.displayName,
        ipAddress: step1Data.ipAddress,
        location: step1Data.location,
        propertyId: step1Data.propertyId ? parseInt(step1Data.propertyId) : null,
        username: step1Data.username,
        sshPort: parseInt(step1Data.sshPort) || 22,
        apiPort: parseInt(step1Data.apiPort) || 8728,
        useSsl: step1Data.useSsl,
        isEnabled: true,
        provisioningStatus: 'Registered'
      };

      // Only include password if it's provided (new router or password changed)
      if (step1Data.password) {
        dto.password = step1Data.password;
      }

      const response = await apiFetch(routerId ? `/api/RouterConfig/${routerId}` : '/api/RouterConfig', {
        method: routerId ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(dto)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to save router');
      }

      const data = await response.json();
      if (data.success) {
        setRouterId(data.data.id);
        toast.success(routerId ? 'Router updated' : 'Router registered');
        return true;
      }
    } catch (err) {
      console.error('Error saving router:', err);
      toast.error(err.message || 'Failed to save router');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleProceedToStep2 = async () => {
    const saved = await saveRouterConfig();
    if (saved) {
      setCurrentStep(2);
    }
  };

  // ==================== STEP 2: Select Services ====================
  const handleProceedToStep3 = async () => {
    setCurrentStep(3);
    loadWireGuardConfig();
  };

  // ==================== STEP 3: Configure VPN ====================
  const loadWireGuardConfig = async () => {
    setStep3Data(prev => ({ ...prev, wireguardLoading: true }));
    try {
      const response = await apiFetch('/api/WireGuard/config');
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setStep3Data(prev => ({
            ...prev,
            wireguardConfig: data.data,
            wireguardLoading: false
          }));
        }
      }
    } catch (err) {
      console.error('Error loading WireGuard config:', err);
      setStep3Data(prev => ({ ...prev, wireguardLoading: false }));
    }
  };

  const addWireGuardPeer = async () => {
    if (!step3Data.newPeerPublicKey) {
      toast.error('Please enter a public key');
      return;
    }

    setStep3Data(prev => ({ ...prev, step3Status: 'adding', step3Error: null }));
    try {
      const peerRequest = {
        wireGuardConfigId: step3Data.wireguardConfig.id,
        siteName: step1Data.displayName, // Auto-populated from router name
        routerConfigId: routerId,
        publicKey: step3Data.newPeerPublicKey,
        vpnIpAddress: '', // Auto-assign
        endpoint: step3Data.newPeerEndpoint || '',
        presharedKey: null,
        persistentKeepalive: parseInt(step3Data.newPeerKeepalive) || 0,
        isEnabled: true,
        notes: `Peer for ${step1Data.displayName}`
      };

      const response = await apiFetch('/api/WireGuard/peer', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(peerRequest)
      });

      const data = await response.json();
      if (data.success) {
        setStep3Data(prev => ({
          ...prev,
          step3Status: 'success',
          newPeerPublicKey: '',
          newPeerEndpoint: '',
          newPeerKeepalive: '25'
        }));
        toast.success('WireGuard peer added successfully');
        loadWireGuardConfig();
        return true;
      } else {
        throw new Error(data.message || 'Failed to add peer');
      }
    } catch (err) {
      setStep3Data(prev => ({
        ...prev,
        step3Status: 'failed',
        step3Error: err.message
      }));
      toast.error('Failed to add peer: ' + err.message);
      return false;
    }
  };

  const handleProceedToStep4 = async () => {
    if (step3Data.step3Status !== 'success') {
      const added = await addWireGuardPeer();
      if (added) {
        setCurrentStep(4);
        verifySetup();
      }
    } else {
      setCurrentStep(4);
      verifySetup();
    }
  };

  const handleStartProvisioning = async () => {
    if (!routerId) {
      toast.error('Router ID not found');
      return;
    }

    setStep4Data(prev => ({ ...prev, isProvisioning: true, provisioningResult: null }));

    try {
      const response = await apiFetch(`/api/RouterProvisioning/${routerId}/provision-conditional`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(step2Data.provisioningOptions)
      });

      const data = await response.json();
      setStep4Data(prev => ({ ...prev, provisioningResult: data, isProvisioning: false }));

      if (data.success) {
        toast.success('Router provisioned successfully!');
      } else {
        toast.error(`Provisioning failed: ${data.message}`);
      }
    } catch (error) {
      console.error('Error during provisioning:', error);
      toast.error('Failed to provision router');
      setStep4Data(prev => ({
        ...prev,
        isProvisioning: false,
        provisioningResult: { success: false, message: error.message }
      }));
    }
  };

  // ==================== STEP 4: Verify & Provision ====================
  const verifySetup = async () => {
    const verification = {};
    try {
      // Check router
      const routerResponse = await apiFetch(`/api/RouterConfig/${routerId}`);
      if (routerResponse.ok) {
        const data = await routerResponse.json();
        verification.router = data.success;
      }

      // Check WireGuard config
      if (step3Data.wireguardConfig) {
        verification.wireguard = true;
      }

      setStep4Data(prev => ({
        ...prev,
        verificationStatus: verification,
        allChecksPass: Object.values(verification).every(v => v === true)
      }));
    } catch (err) {
      console.error('Error verifying setup:', err);
      setStep4Data(prev => ({
        ...prev,
        allChecksPass: false
      }));
    }
  };

  // ==================== RENDER METHODS ====================
  const renderStep1 = () => (
    <div className="wizard-step">
      <h2>Step 1: Register Router</h2>
      <p className="step-description">Enter your MikroTik router details and SSH credentials</p>

      <form className="wizard-form">
        <div className="form-group">
          <label>Display Name *</label>
          <input
            type="text"
            value={step1Data.displayName}
            onChange={(e) => handleStep1Change('displayName', e.target.value)}
            placeholder="e.g., Alexandra Plaza"
            required
          />
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>IP Address *</label>
            <input
              type="text"
              value={step1Data.ipAddress}
              onChange={(e) => handleStep1Change('ipAddress', e.target.value)}
              placeholder="e.g., 192.168.1.1"
              required
            />
          </div>
          <div className="form-group">
            <label>Location</label>
            <input
              type="text"
              value={step1Data.location}
              onChange={(e) => handleStep1Change('location', e.target.value)}
              placeholder="e.g., Server Room A"
            />
          </div>
        </div>

        <div className="form-group">
          <label>Property</label>
          <select
            value={step1Data.propertyId}
            onChange={(e) => handleStep1Change('propertyId', e.target.value)}
          >
            <option value="">-- Select Property --</option>
            {properties.map(prop => (
              <option key={prop.propertyID || prop.id} value={prop.propertyID || prop.id}>{prop.propertyName || prop.name}</option>
            ))}
          </select>
        </div>

        <div className="form-divider">SSH Credentials</div>

        <div className="form-row">
          <div className="form-group">
            <label>SSH Username *</label>
            <input
              type="text"
              value={step1Data.username}
              onChange={(e) => handleStep1Change('username', e.target.value)}
              placeholder="e.g., admin"
              required
            />
          </div>
          <div className="form-group">
            <label>SSH Password {!isEditing && '*'}</label>
            <input
              type="password"
              value={step1Data.password}
              onChange={(e) => handleStep1Change('password', e.target.value)}
              placeholder={isEditing ? '(Leave blank to keep existing password)' : 'Password'}
              required={!isEditing}
            />
            {isEditing && (
              <small className="form-helper">Leave blank to keep the existing password. Enter a new password to change it.</small>
            )}
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>SSH Port</label>
            <input
              type="number"
              value={step1Data.sshPort}
              onChange={(e) => handleStep1Change('sshPort', e.target.value)}
              placeholder="22"
            />
          </div>
          <div className="form-group">
            <label>API Port</label>
            <input
              type="number"
              value={step1Data.apiPort}
              onChange={(e) => handleStep1Change('apiPort', e.target.value)}
              placeholder="8728"
            />
          </div>
        </div>

        <div className="form-group checkbox">
          <label>
            <input
              type="checkbox"
              checked={step1Data.useSsl}
              onChange={(e) => handleStep1Change('useSsl', e.target.checked)}
            />
            Use SSL for API
          </label>
        </div>
      </form>
    </div>
  );

  const renderStep2 = () => (
    <div className="wizard-step">
      <h2>Step 2: Select Provisioning Services</h2>
      <p className="step-description">Choose which services to enable during router provisioning</p>
      <ProvisioningServiceSelector
        onOptionsChange={(options) => setStep2Data(prev => ({ ...prev, provisioningOptions: options }))}
        initialOptions={step2Data.provisioningOptions}
      />
    </div>
  );

  const renderStep3 = () => (
    <div className="wizard-step">
      <h2>Step 3: Configure VPN (WireGuard)</h2>
      <p className="step-description">Add this router as a WireGuard peer</p>

      {step3Data.wireguardLoading ? (
        <div className="loading"><FaSpinner className="spinner" /> Loading WireGuard config...</div>
      ) : step3Data.wireguardConfig ? (
        <div className="wireguard-setup">
          <div className="config-info">
            <h3>Server Configuration</h3>
            <div className="info-row">
              <span>Server Address:</span>
              <strong>{step3Data.wireguardConfig.serverAddress}</strong>
            </div>
            <div className="info-row">
              <span>Public Key:</span>
              <code>{step3Data.wireguardConfig.publicKey}</code>
            </div>
            <div className="info-row">
              <span>Listen Port:</span>
              <strong>{step3Data.wireguardConfig.listenPort}</strong>
            </div>
          </div>

          <div className="peer-form">
            <h3>Add Peer: {step1Data.displayName}</h3>

            <div className="form-group">
              <label>Site Name (Read-only)</label>
              <input type="text" value={step1Data.displayName} disabled />
            </div>

            <div className="form-group">
              <label>Public Key *</label>
              <textarea
                value={step3Data.newPeerPublicKey}
                onChange={(e) => setStep3Data(prev => ({ ...prev, newPeerPublicKey: e.target.value }))}
                placeholder="Paste the peer's public key here (generated with 'wg genkey | wg pubkey')"
                rows={3}
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Endpoint (Optional)</label>
                <input
                  type="text"
                  value={step3Data.newPeerEndpoint}
                  onChange={(e) => setStep3Data(prev => ({ ...prev, newPeerEndpoint: e.target.value }))}
                  placeholder="e.g., peer.example.com:13231"
                />
              </div>
              <div className="form-group">
                <label>Persistent Keepalive (seconds)</label>
                <input
                  type="number"
                  value={step3Data.newPeerKeepalive}
                  onChange={(e) => setStep3Data(prev => ({ ...prev, newPeerKeepalive: e.target.value }))}
                  placeholder="25 (for NAT traversal)"
                />
              </div>
            </div>

            <button
              className="btn-add-peer"
              onClick={() => addWireGuardPeer()}
              disabled={step3Data.step3Status === 'adding'}
            >
              {step3Data.step3Status === 'adding' ? (
                <><FaSpinner className="spinner" /> Adding Peer...</>
              ) : (
                <>Add WireGuard Peer</>
              )}
            </button>

            {step3Data.step3Status === 'success' && (
              <div className="peer-status success">
                <FaCheckCircle /> Peer added successfully
              </div>
            )}

            {step3Data.step3Status === 'failed' && (
              <div className="peer-status error">
                <FaExclamationTriangle /> {step3Data.step3Error}
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="error">Failed to load WireGuard configuration</div>
      )}
    </div>
  );

  const renderStep4 = () => (
    <div className="wizard-step">
      <h2>Step 4: Verify & Provision</h2>
      <p className="step-description">Review configuration and start automated provisioning</p>

      {!step4Data.provisioningResult && !step4Data.isProvisioning && (
        <div className="verification-summary">
          <div className="check-item">
            <div className={`check-status success`}>
              <FaCheckCircle /> Router Registered
            </div>
            <p className="check-detail">{step1Data.displayName} ({step1Data.ipAddress})</p>
          </div>

          <div className="check-item">
            <div className={`check-status success`}>
              <FaCheckCircle /> Services Selected
            </div>
            <p className="check-detail">
              {[
                step2Data.provisioningOptions.enableWireGuard && 'WireGuard',
                step2Data.provisioningOptions.enableRadiusAuth && 'RADIUS',
                step2Data.provisioningOptions.enableSnmpMonitoring && 'SNMP',
                step2Data.provisioningOptions.enableAddressListSync && 'Address Lists',
                step2Data.provisioningOptions.enableFirewallTemplateSync && 'Firewall'
              ].filter(Boolean).join(', ')}
            </p>
          </div>

          <div className="check-item">
            <div className={`check-status ${step3Data.step3Status === 'success' ? 'success' : 'pending'}`}>
              <FaCheckCircle /> WireGuard Peer Configured
            </div>
            <p className="check-detail">VPN tunnel ready for provisioning</p>
          </div>
        </div>
      )}

      {(step4Data.provisioningResult || step4Data.isProvisioning) && (
        <ProvisioningProgressDisplay
          result={step4Data.provisioningResult}
          isProvisioning={step4Data.isProvisioning}
        />
      )}

      {!step4Data.provisioningResult && !step4Data.isProvisioning && (
        <div className="provisioning-notice">
          <p><strong>Ready to provision!</strong> The wizard will now execute the automated provisioning workflow (11 steps) to configure your router with the selected services.</p>
          <button
            className="btn btn-provision"
            onClick={handleStartProvisioning}
            disabled={step4Data.isProvisioning}
          >
            {step4Data.isProvisioning ? (
              <><FaSpinner className="spinner" /> Starting Provisioning...</>
            ) : (
              <>Start Provisioning</>
            )}
          </button>
        </div>
      )}
    </div>
  );

  return (
    <div className="router-setup-wizard">
      <div className="wizard-header">
        <h1>Router Setup & Management Wizard</h1>
        <div className="progress-indicator">
          {[1, 2, 3, 4].map(step => (
            <div
              key={step}
              className={`progress-step ${currentStep === step ? 'active' : ''} ${currentStep > step ? 'completed' : ''}`}
              onClick={() => currentStep > step && setCurrentStep(step)}
            >
              {currentStep > step ? <FaCheck /> : step}
              <span>{['Register', 'Services', 'VPN', 'Provision'][step - 1]}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="wizard-content">
        {currentStep === 1 && renderStep1()}
        {currentStep === 2 && renderStep2()}
        {currentStep === 3 && renderStep3()}
        {currentStep === 4 && renderStep4()}
      </div>

      <div className="wizard-footer">
        <button
          className="btn btn-secondary"
          onClick={() => currentStep > 1 && setCurrentStep(currentStep - 1)}
          disabled={currentStep === 1}
        >
          <FaArrowLeft /> Previous
        </button>

        <button
          className="btn btn-primary"
          onClick={() => {
            if (currentStep === 1) handleProceedToStep2();
            else if (currentStep === 2) handleProceedToStep3();
            else if (currentStep === 3) handleProceedToStep4();
            else if (currentStep === 4 && step4Data.provisioningResult?.success) onComplete?.();
          }}
          disabled={saving || loading || step4Data.isProvisioning || (!step4Data.provisioningResult && currentStep === 4)}
        >
          {currentStep === 4 && step4Data.provisioningResult?.success ? (
            <><FaCheck /> Complete</>
          ) : currentStep === 4 ? (
            <>Provisioning...</>
          ) : (
            <>{currentStep === 1 && saving ? <FaSpinner className="spinner" /> : ''} Next <FaArrowRight /></>
          )}
        </button>
      </div>
    </div>
  );
};

export default RouterSetupWizard;
